import { useEffect, useRef } from "react";
import { useReducedMotion } from "framer-motion";
import { isLowPower, isSlowConnection } from "../utils/perf";

// Polygonal-framed ambient YouTube panel (owner-provided clips). The player
// is created only when the panel nears the viewport and pauses when it
// leaves — never downloads on slow/data-saver connections.
export const YTVideoPanel = ({ videoId, label, testid, className = "" }) => {
  const host = useRef(null);
  const player = useRef(null);
  const reduce = useReducedMotion();
  const low = isLowPower();

  useEffect(() => {
    const el = host.current;
    if (!el || reduce || low || isSlowConnection()) return undefined;
    let poll;

    const mount = () => {
      if (player.current) return;
      if (!window.YT || !window.YT.Player) {
        // the background-music component loads the API; wait for it
        poll = setInterval(() => {
          if (window.YT && window.YT.Player) {
            clearInterval(poll);
            mount();
          }
        }, 400);
        return;
      }
      player.current = new window.YT.Player(el, {
        videoId,
        playerVars: {
          autoplay: 1,
          mute: 1,
          controls: 0,
          loop: 1,
          playlist: videoId,
          playsinline: 1,
          rel: 0,
          modestbranding: 1,
          disablekb: 1,
          fs: 0,
        },
      });
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          mount();
          try {
            player.current && player.current.playVideo();
          } catch {
            /* not ready yet */
          }
        } else if (player.current) {
          try {
            player.current.pauseVideo();
          } catch {
            /* ignore */
          }
        }
      },
      { rootMargin: "250px" }
    );
    io.observe(el);
    return () => {
      io.disconnect();
      if (poll) clearInterval(poll);
      try {
        player.current && player.current.destroy();
      } catch {
        /* ignore */
      }
      player.current = null;
    };
  }, [videoId, reduce, low]);

  return (
    <div data-testid={testid} className={`poly-frame ${className}`} aria-label={label}>
      <div className="poly-frame-inner">
        <div ref={host} className="h-full w-full [&_iframe]:h-full [&_iframe]:w-full" />
      </div>
    </div>
  );
};
